import React, { useCallback, useContext, useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { Feature } from "geojson";
import { enqueueSnackbar } from "notistack";

import { selectLayerByVersionAndLayerKey } from "fond/api";
import { mergeLayerAttributes } from "fond/draw/attributeHelpers";
import FeatureAttributesForm from "fond/draw/FeatureAttributesForm";
import { HistoryContext, useHistory } from "fond/history";
import { MapContext } from "fond/map/MapProvider";
import { useAppSelector } from "fond/utils/hooks";

const PropertiesPanelEdit: React.FC = () => {
  const { map, drawControl } = useContext(MapContext);
  const { set: setHistory } = useHistory(HistoryContext);
  const [selectedFeature, setSelectedFeature] = useState<Feature | null>(null);
  const versionId = useAppSelector((state) => state.project.versionId);

  // Get editingLayerId from drawControl options
  const editingLayerId = drawControl.current?.options?.editOptions?.editingLayerId;

  // Get the layer configuration with attributes
  const layerConfig = useAppSelector((state) =>
    editingLayerId ? selectLayerByVersionAndLayerKey(state, { versionId, layerId: editingLayerId }) : undefined
  );

  // Get the current EditingLayer configuration
  const editingLayer = drawControl.current?.options?.editOptions?.allLayers?.find((layer) => layer.ID === editingLayerId);

  const handleOnSelect = useCallback(() => {
    if (drawControl.current?.getSelectedIds().length === 1) {
      const feature = drawControl.current?.getSelected().features[0];
      setSelectedFeature(feature || null);
    } else {
      setSelectedFeature(null);
    }
  }, [drawControl]);

  // TODO - handle updates from the AttributeFormPopup
  // maybe a custom event to monito?

  useEffect(() => {
    map?.on("draw.selectionchange", handleOnSelect);

    return () => {
      map?.off("draw.selectionchange", handleOnSelect);
    };
  }, [map, handleOnSelect]);

  const handleSubmit = useCallback(
    (_values: Record<string, string | number | boolean>) => {
      // Update history silently after attribute values are set
      if (drawControl.current?.getAll()) {
        setHistory(drawControl.current.getAll(), { silent: true });
      }

      // Refresh the selected feature after save
      if (selectedFeature?.id && drawControl.current) {
        const updatedFeature = drawControl.current.get(selectedFeature.id as string);
        if (updatedFeature) {
          setSelectedFeature(updatedFeature as Feature);
        }
      }
      enqueueSnackbar("Feature attributes updated", { variant: "success" });
    },
    [selectedFeature, drawControl, setHistory]
  );

  if (!selectedFeature) {
    return (
      <Box height="100%" display="flex" alignItems="center" justifyContent="center" p={2}>
        <Typography variant="body2" color="textSecondary">
          Select a feature to edit properties
        </Typography>
      </Box>
    );
  }

  // Merge attributes from EditingLayer with layer schema
  const attributes = mergeLayerAttributes(layerConfig?.Attributes, editingLayer);

  if (attributes.length === 0) {
    return (
      <Box height="100%" display="flex" alignItems="center" justifyContent="center" p={2}>
        <Typography variant="body2" color="textSecondary">
          This layer has no editable attributes
        </Typography>
      </Box>
    );
  }

  return (
    <Box height="100%" display="flex" flexDirection="column">
      <Box p={2} pb={1}>
        <Typography variant="subtitle2" gutterBottom>
          {layerConfig?.Configuration.Label || editingLayerId}
        </Typography>
      </Box>
      <Box flex={1} sx={{ minHeight: 0 }}>
        <FeatureAttributesForm feature={selectedFeature} attributes={attributes} onSubmit={handleSubmit} showReset disableOnPristine />
      </Box>
    </Box>
  );
};

export default PropertiesPanelEdit;
